import IconPlusSmall from "central-icons/IconPlusSmall";
import { useCallback, useContext, useEffect, useState, type FC } from "react";

import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { withGitpodSize } from "@/assets/icons/utils";
import { Button } from "@/components/flexkit/Button";
import { Heading2 } from "@/components/flexkit/typography/Headings";
import { Text } from "@/components/flexkit/typography/Text";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useListPersonalAccessTokens, type PlainPersonalAccessToken } from "@/queries/personal-access-tokens-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { DeletePersonalAccessTokenModal } from "@/routes/settings/user/personal-access-tokens/DeletePersonalAccessTokenModal";
import { NewPersonalAccessTokenModal } from "@/routes/settings/user/personal-access-tokens/NewPersonalAccessTokenModal";
import { PersonalAccessTokensList } from "@/routes/settings/user/personal-access-tokens/PersonalAccessTokensList";

export type PersonalAccessTokensPageProps = {
    /** When true, hides the description text (used in modal context where description is provided externally) */
    hideDescription?: boolean;
};

export const PersonalAccessTokensPage: FC<PersonalAccessTokensPageProps> = ({ hideDescription = false }) => {
    useDocumentTitle("Personal Access Tokens");

    const { data: user } = useAuthenticatedUser();
    const { setBreadCrumbRowAction } = useContext(SettingsPageContext);

    const {
        data: patsData,
        error: patsError,
        isPending: isPendingPATs,
    } = useListPersonalAccessTokens({ userId: user?.id || "" });

    const [showNewPATModal, setShowNewPATModal] = useState(false);
    const [patToDelete, setPatToDelete] = useState<PlainPersonalAccessToken | undefined>();

    useEffect(() => {
        if (patsData?.pats?.length === 0) {
            return;
        }

        setBreadCrumbRowAction(
            <Button
                size="sm"
                data-testid="new-token-button"
                LeadingIcon={withGitpodSize(IconPlusSmall)}
                onClick={() => setShowNewPATModal(true)}
                data-tracking-id="show-new-pat-modal-personal-access-token-page"
            >
                New Token
            </Button>,
        );

        return () => {
            setBreadCrumbRowAction(null);
        };
    }, [patsData?.pats, setBreadCrumbRowAction]);

    let content: React.ReactNode | null = null;
    if (patsError) {
        content = <FailedToLoad />;
    } else if (isPendingPATs) {
        content = <SkeletonBlock ready={false} failed={!!patsError} className="h-[160px] w-full" />;
    } else {
        content = (
            <PersonalAccessTokensList
                tokens={patsData.pats ?? []}
                onNew={() => setShowNewPATModal(true)}
                onDelete={(pat) => setPatToDelete(pat)}
            />
        );
    }

    return (
        <div data-testid="personal-access-tokens-page" className={hideDescription ? "" : "flex flex-col gap-6"}>
            {!hideDescription && (
                <Text className="max-w-3xl text-balance text-base text-content-secondary">
                    Personal access tokens (PATs) are secure credentials that allow API access to Ona without manually
                    logging in. They enable integration with external tools and workflow automation, while providing
                    users the ability to revoke access when needed.
                </Text>
            )}
            {content}
            {showNewPATModal && <NewPersonalAccessTokenModal onClose={() => setShowNewPATModal(false)} />}
            {patToDelete && (
                <DeletePersonalAccessTokenModal pat={patToDelete} onClose={() => setPatToDelete(undefined)} />
            )}
        </div>
    );
};

const FailedToLoad: FC = () => {
    const onReload = useCallback(() => {
        window.location.reload();
    }, []);

    return (
        <div
            className="flex flex-col items-center gap-5 p-12"
            data-testid="error-failed-to-load-personal-access-tokens-data"
        >
            <div className="flex flex-col items-center gap-1">
                <Heading2 className="text-xl font-bold text-content-primary">
                    We&apos;re having trouble loading the personal access tokens.
                </Heading2>
                <Text className="text-lg text-content-secondary">
                    You can try refreshing the page. If the problem persists, please contact support.
                </Text>
            </div>
            <Button
                variant="secondary"
                LeadingIcon={IconRefresh}
                onClick={onReload}
                data-tracking-id="refresh-personal-access-tokens-page"
            >
                Refresh Page
            </Button>
        </div>
    );
};
